import React, { useEffect, useState } from 'react';
import { Layers, Save } from 'lucide-react';
import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import { optimizedApi } from '@/utils/api/optimized-api';
import { showToast } from '@/components/ui/Toast';
import { isMasterUser } from '@/utils/master';
import { LeadProfileSectionKey, LeadProfileSettings } from '@/types/leads/Profile';

const SECTION_OPTIONS: { key: LeadProfileSectionKey; label: string; description: string }[] = [
    { key: 'academic', label: 'Student', description: 'Academic year, name, grade, curriculum, DOB, gender' },
    { key: 'guardian', label: 'Guardian', description: "Existing-parent flag, parents' occupation, company, education" },
    { key: 'residence', label: 'Residence', description: 'City, country, address, local/non-local' },
    { key: 'school_history', label: 'School History', description: 'Up to 2 previous schools — name, city, board, reason for leaving' },
    { key: 'references', label: 'References', description: 'Up to 2 references — name, phone, relationship' },
    { key: 'brand_awareness', label: 'Brand Awareness', description: 'How the family heard about the school (multi-select)' },
    { key: 'communication', label: 'Lead Source & Communication', description: 'Source category/sub-category, contactability, call/message log' },
    { key: 'qualification_status', label: 'Qualification & Status', description: 'Counsellor qualification, overall status, priority, stage of admission' },
];

const LeadProfileSettingsPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [sectionsEnabled, setSectionsEnabled] = useState<LeadProfileSectionKey[]>([]);
    const [dependentSourceEnabled, setDependentSourceEnabled] = useState(false);
    const [clientName, setClientName] = useState<string | null>(null);

    const isMaster = isMasterUser();

    useEffect(() => {
        optimizedApi.get<LeadProfileSettings>('/lead-profile/settings').then((res) => {
            if (res.success && res.data) {
                setIsActive(res.data.is_active);
                setSectionsEnabled(res.data.sections_enabled || []);
                setDependentSourceEnabled(res.data.dependent_source_enabled);
            }
            setLoading(false);
        }).catch(() => setLoading(false));

        try {
            const userStr = localStorage.getItem('crm_user');
            if (userStr) setClientName(JSON.parse(userStr).client_name || null);
        } catch { /* non-fatal */ }
    }, []);

    const toggleSection = (key: LeadProfileSectionKey) => {
        setSectionsEnabled((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const handleSave = async () => {
        setSaving(true);
        const res = await optimizedApi.put('/lead-profile/settings', {
            sections_enabled: sectionsEnabled,
            dependent_source_enabled: dependentSourceEnabled,
            is_active: isActive,
        });
        setSaving(false);
        if (res.success) {
            showToast('Lead profile settings saved', 'success');
        } else {
            showToast(res.message || 'Failed to save settings', 'error');
        }
    };

    if (!isMaster) {
        return (
            <div className="p-8 text-center text-gray-500">
                Only YellowSlate admins can configure the extended lead profile.
            </div>
        );
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-400">Loading…</div>;
    }

    return (
        <div className="space-y-6">
            <ModernSettingHeader
                title="Extended Lead Profile"
                description="Turn on the sectioned lead profile (Child Details, Communication, Qualification & Status) for the school you are currently viewing as."
                icon={<Layers className="h-5 w-5" />}
            />

            <div className="p-6 space-y-6 max-w-3xl">
                {clientName && (
                    <p className="text-sm text-gray-500">
                        Configuring for: <span className="font-semibold text-gray-700">{clientName}</span> — switch accounts under Settings → Accounts to configure a different school.
                    </p>
                )}

                <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-5">
                    <div>
                        <p className="font-semibold text-gray-800">Enable extended lead profile</p>
                        <p className="text-sm text-gray-500 mt-1">
                            Off by default. Turning this on only changes this school's lead form — every other client keeps today's form, untouched.
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                        onClick={() => setIsActive((v) => !v)}
                        style={{
                            position: 'relative',
                            width: 44,
                            height: 24,
                            borderRadius: 999,
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            flexShrink: 0,
                            backgroundColor: isActive ? 'var(--brand-color, #02338A)' : '#d1d5db',
                            transition: 'background-color 0.2s ease',
                        }}
                    >
                        <span
                            style={{
                                position: 'absolute',
                                top: 2,
                                left: isActive ? 22 : 2,
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#ffffff',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                                transition: 'left 0.2s ease',
                            }}
                        />
                    </button>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                    {SECTION_OPTIONS.map((section) => {
                        const checked = sectionsEnabled.includes(section.key);
                        return (
                            <label key={section.key} className="flex items-start gap-3 p-4 cursor-pointer hover:bg-gray-50">
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleSection(section.key)}
                                    className="mt-1 h-4 w-4"
                                />
                                <div>
                                    <p className="font-medium text-gray-800 text-sm">{section.label}</p>
                                    <p className="text-xs text-gray-500 mt-0.5">{section.description}</p>
                                </div>
                            </label>
                        );
                    })}
                </div>

                <label className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={dependentSourceEnabled}
                        onChange={(e) => setDependentSourceEnabled(e.target.checked)}
                        className="mt-1 h-4 w-4"
                    />
                    <div>
                        <p className="font-semibold text-gray-800">Dependent Lead Source options</p>
                        <p className="text-sm text-gray-500 mt-1">
                            When on, Lead Source Sub-category options are filtered by the selected Category (Offline / Online / Direct).
                        </p>
                    </div>
                </label>

                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 bg-brandColor text-white rounded-lg text-sm font-semibold disabled:opacity-50"
                >
                    <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Changes'}
                </button>
            </div>
        </div>
    );
};

export default LeadProfileSettingsPage;
